import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Paper,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Code as CodeIcon,
  ChatBubbleOutline as ChatIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';

interface QuerySectionProps {
  queryType: 'nl' | 'sql';
  onQueryTypeChange: (type: 'nl' | 'sql') => void;
  query: string;
  onQueryChange: (query: string) => void;
  onExecuteQuery: () => void;
  onBackToStart?: () => void; // Optional callback for the Back to Start button
}

const QuerySection: React.FC<QuerySectionProps> = ({
  queryType,
  onQueryTypeChange,
  query,
  onQueryChange,
  onExecuteQuery,
  onBackToStart,
}) => {
  const [isExecuting, setIsExecuting] = useState(false);

  const handleTypeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newType: 'nl' | 'sql' | null
  ) => {
    if (newType !== null) {
      onQueryTypeChange(newType);
    }
  };

  const handleExecute = async () => {
    setIsExecuting(true);
    try {
      await onExecuteQuery();
    } finally {
      setIsExecuting(false);
    }
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h5">Query Your Data</Typography>
        </Box>
        <ToggleButtonGroup
          value={queryType}
          exclusive
          onChange={handleTypeChange}
          aria-label="query type"
          size="small"
          sx={{ ml: { xs: 0, sm: 2 }, mt: { xs: 1, sm: 0 } }}
        >
          <ToggleButton value="nl" aria-label="natural language">
            <Tooltip title="Natural Language Query">
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ChatIcon sx={{ mr: 0.5 }} />
                <Typography variant="body2">Natural Language</Typography>
              </Box>
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="sql" aria-label="sql">
            <Tooltip title="Direct SQL Query">
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CodeIcon sx={{ mr: 0.5 }} />
                <Typography variant="body2">SQL</Typography>
              </Box>
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <TextField
          fullWidth
          multiline
          rows={3}
          variant="outlined"
          placeholder={
            queryType === 'nl'
              ? 'Enter your question in natural language, e.g., "What is the latest departure date for Omni San Diego Hotel?"'
              : 'Enter SQL query, e.g., "SELECT MAX(departure_date) FROM booking_data WHERE hotel_name = \'Omni San Diego Hotel\'"'
          }
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          sx={{ mb: 2 }}
        />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleExecute}
            disabled={!query.trim() || isExecuting}
            startIcon={isExecuting ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
          >
            {isExecuting ? 'Running...' : 'Run Query'}
          </Button>
        </Box>
      </Paper>

      <Typography variant="body2" color="text.secondary">
        {queryType === 'nl'
          ? 'Enter your question in natural language. The system will convert it to SQL using OpenAI and execute it.'
          : 'Write a SQL query directly. The query will be executed against the in-memory SQLite database.'}
      </Typography>
    </Box>
  );
};

export default QuerySection;
